import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Upload, 
  Search, 
  Trash2, 
  Edit2,
  Phone,
  Mail,
  Building,
  X,
  FileSpreadsheet,
  RotateCcw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", company: "" });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (statusFilter !== "all") params.append("status", statusFilter);
      
      const response = await axios.get(`${API}/contacts?${params}`);
      setContacts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error("Fehler beim Laden der Kontakte");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, formData);
        toast.success("Kontakt aktualisiert");
      } else {
        await axios.post(`${API}/contacts`, formData);
        toast.success("Kontakt hinzugefügt");
      }
      setShowAddDialog(false);
      setEditingContact(null);
      setFormData({ name: "", phone: "", email: "", company: "" });
      fetchContacts();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Kontakt wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/contacts/${id}`);
      toast.success("Kontakt gelöscht");
      fetchContacts();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleResetStatus = async (id) => {
    try {
      await axios.post(`${API}/contacts/${id}/reset-status`);
      toast.success("Status zurückgesetzt");
      fetchContacts();
    } catch (error) {
      toast.error("Fehler beim Zurücksetzen");
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm("Alle Kontakte auf 'Neu' zurücksetzen?")) return;
    try {
      const res = await axios.post(`${API}/contacts/reset-all`);
      toast.success(res.data.message);
      fetchContacts();
    } catch (error) {
      toast.error("Fehler beim Zurücksetzen");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/contacts/upload-csv`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${response.data.count} Kontakte importiert`);
      setShowUploadDialog(false);
      fetchContacts();
    } catch (error) {
      toast.error("Fehler beim Import");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openEditDialog = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      phone: contact.phone,
      email: contact.email || "",
      company: contact.company || ""
    });
    setShowAddDialog(true);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      new: { label: "Neu", class: "status-new" },
      called: { label: "Angerufen", class: "status-called" },
      callback: { label: "Rückruf", class: "status-callback" },
      success: { label: "Erfolg", class: "status-success" },
      rejected: { label: "Abgelehnt", class: "status-rejected" }
    };
    const s = statusMap[status] || statusMap.new;
    return <span className={`status-badge ${s.class}`}>{s.label}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" data-testid="contacts-title">
            Kontakte
          </h1>
          <p className="text-slate-500 mt-1">{contacts.length} Kontakte insgesamt</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleResetAll}
            className="btn-press text-slate-600"
            data-testid="reset-all-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Alle zurücksetzen
          </Button>
          <a
            href="/vorlage_kontakte.csv"
            download="vorlage_kontakte.csv"
            className="text-xs text-[#405189] hover:text-[#2d3a5e] underline underline-offset-2 decoration-dashed font-medium"
            data-testid="download-csv-template"
          >
            CSV-Vorlage
          </a>
          <Button 
            variant="outline" 
            onClick={() => setShowUploadDialog(true)}
            className="btn-press"
            data-testid="upload-csv-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            CSV Import
          </Button>
          <Button 
            onClick={() => {
              setEditingContact(null);
              setFormData({ name: "", phone: "", email: "", company: "" });
              setShowAddDialog(true);
            }}
            className="bg-slate-900 hover:bg-slate-800 btn-press"
            data-testid="add-contact-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Kontakt hinzufügen
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suchen nach Name, Telefon, Firma..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder="Status Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="new">Neu</SelectItem>
                <SelectItem value="called">Angerufen</SelectItem>
                <SelectItem value="callback">Rückruf</SelectItem>
                <SelectItem value="success">Erfolg</SelectItem>
                <SelectItem value="rejected">Abgelehnt</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contacts Table */}
      <Card className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)] overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="contacts-table">
            <TableHeader>
              <TableRow className="bg-slate-50/50 border-b border-slate-200">
                <TableHead className="font-semibold text-slate-700">Name</TableHead>
                <TableHead className="font-semibold text-slate-700">Telefon</TableHead>
                <TableHead className="font-semibold text-slate-700 hidden md:table-cell">E-Mail</TableHead>
                <TableHead className="font-semibold text-slate-700 hidden lg:table-cell">Firma</TableHead>
                <TableHead className="font-semibold text-slate-700">Status</TableHead>
                <TableHead className="font-semibold text-slate-700 text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <div className="h-12 bg-slate-100 animate-pulse rounded"></div>
                    </TableCell>
                  </TableRow>
                ))
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <div className="empty-state">
                      <FileSpreadsheet className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500">Keine Kontakte gefunden</p>
                      <p className="text-slate-400 text-sm mt-1">
                        Fügen Sie Kontakte hinzu oder importieren Sie eine CSV-Datei
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow 
                    key={contact.id} 
                    className="table-row-hover border-b border-slate-100"
                    data-testid={`contact-row-${contact.id}`}
                  >
                    <TableCell className="font-medium text-slate-900">
                      {contact.name}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="w-3.5 h-3.5 text-slate-400" />
                        {contact.phone}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {contact.email ? (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-3.5 h-3.5 text-slate-400" />
                          {contact.email}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {contact.company ? (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Building className="w-3.5 h-3.5 text-slate-400" />
                          {contact.company}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(contact.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {contact.status !== "new" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleResetStatus(contact.id)}
                            className="h-8 w-8 text-slate-500 hover:text-[#0ab39c]"
                            title="Status zurücksetzen"
                            data-testid={`reset-contact-${contact.id}`}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(contact)}
                          className="h-8 w-8 text-slate-500 hover:text-slate-700"
                          data-testid={`edit-contact-${contact.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(contact.id)}
                          className="h-8 w-8 text-slate-500 hover:text-red-600"
                          data-testid={`delete-contact-${contact.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle className="">
              {editingContact ? "Kontakt bearbeiten" : "Neuer Kontakt"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Max Mustermann"
                required
                data-testid="contact-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Telefon *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+49 123 456789"
                required
                data-testid="contact-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="max@beispiel.de"
                data-testid="contact-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Firma</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Firma GmbH"
                data-testid="contact-company-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Abbrechen
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-contact-btn">
                {editingContact ? "Speichern" : "Hinzufügen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="">CSV Import</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Laden Sie eine CSV-Datei mit den Spalten: <code className="bg-slate-100 px-1 rounded">name</code>, <code className="bg-slate-100 px-1 rounded">phone</code>, <code className="bg-slate-100 px-1 rounded">email</code> (optional), <code className="bg-slate-100 px-1 rounded">company</code> (optional)
            </p>
            <div 
              className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center cursor-pointer hover:border-slate-400 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600">Klicken zum Hochladen oder Datei hierher ziehen</p>
              <p className="text-sm text-slate-400 mt-1">CSV-Dateien bis 10MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleUpload}
                data-testid="csv-file-input"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
